import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Message, SharedServiceConfig } from './shared-service.types';
import { saveAs } from 'file-saver';


@Injectable({
  providedIn: 'root',
})
export class MessageService {
  constructor(private httpClient: HttpClient,
    @Inject('sharedServiceConfig') private sharedServiceConfig: SharedServiceConfig) { }

  private restServer = this.sharedServiceConfig.restServer;

  /**
   * Get a list of messages by object type and id
  **/
  getMessages(type: string, id: number): Observable<Message[]> {
    return this.httpClient.get<Message[]>(
      `${this.restServer}/core_messaging/message/get_for_object/?type=${type}&id=${id}`
    ).pipe(map((result) => <Message[]>result))
  }

  /**
   * Get a message by message key
  **/
  getMessageFromKey(messageKey: string): Observable<Message> {
    return this.httpClient.get<Message>(
      `${this.restServer}/core_messaging/message/get_for_message_key/?message_key=${messageKey}`,
      { headers: { 'skip-error-interceptor': 'true' } }
    ).pipe(map((result) => <Message>result))
  }
}


@Injectable({
  providedIn: 'root',
})
export class XlsxService {
  constructor(private httpClient: HttpClient,
    @Inject('xlsxServiceConfig') private xlsxServiceConfig: SharedServiceConfig,
  ) {}

  private restServer = this.xlsxServiceConfig.restServer;
  
  /**
   * Downloads an excel template document with controlled dropdowns for a sample sheet.
   * @param dataSchemaId ID of dataSchema associated with sample sheet template
   * @param filename Optional name for the file
   */
  getXlsxTemplate(dataSchemaId: number, filename?: string) {
    const downloadFilename = filename ? filename + '.xlsx' : 'sample_sheet.xlsx';
    const url = this.restServer + '/metadata_repository/sample_sheet_template/get_excel_file/?data_schema_id=' + dataSchemaId;

    this.httpClient.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => saveAs(blob, downloadFilename),
      error: (error) => console.error('Error downloading sample sheet:', error)
    });
  }

  /**
   * Downloads an excel sample template document using the iLab submission sheet format, including controlled dropdowns.
   * @param assayId Required assay id for downloading the iLab submission form based on a sample sheet
   * @param filename Optional name for the file
   */
  getiLabSheet(assayId: number, filename?: string) {
    const downloadFilename = filename ? filename + '.xlsx' : 'ilab_sample_sheet.xlsx';
    const url = this.restServer + '/metadata_repository/sample_sheet_template/get_excel_file/?assay_id=' + assayId;

    this.httpClient.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => saveAs(blob, downloadFilename),
      error: (error) => console.error('Error downloading iLab sheet:', error)
    });
  }
}


@Injectable({
  providedIn: 'root',
})
export class CsvDownloadService {
  downloadCsv<T>(
    data: T[],
    fileName: string,
    firstColumn?: string,
    includedColumns: string[] = [],
    headerMap: { [original: string]: string } = {},
    includedHeaders?: string[]
  ) {
    const csv = this.convertToCsv(data, firstColumn, includedColumns, headerMap, includedHeaders);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, fileName);
  }

  convertToCsv(
    data: any[],
    firstColumn?: string,
    includedColumns: string[] = [],
    headerMap: { [original: string]: string } = {},
    includedHeaders?: string[]
  ) {
    if (data.length === 0) return '';
    // Use specified columns or default to all headers from the first row
    let originalHeaders = Object.keys(data[0]);
    if (includedColumns.length > 0) {
      originalHeaders = includedColumns;
    }

    const filteredHeaders = originalHeaders.map((h) => headerMap[h] || h);

    // Optionally move the first column to the front
    if (firstColumn) {
      const nameIndex = filteredHeaders.indexOf(firstColumn);
      if (nameIndex > 0) {
        filteredHeaders.splice(nameIndex, 1);
        filteredHeaders.unshift(firstColumn);
      }
    }

    let headerRow: string[] = [];
    if (includedHeaders) {
      headerRow = includedHeaders;
    }
    else {
      headerRow = filteredHeaders;
    }

    const csvRows = [
      headerRow.join(','),
      ...data.map((row) =>
        filteredHeaders
          .map((mappedHeader) => {
            // for a mapped header like { derives_from: 'source' }, headerEntry will be ['derives_from', 'source']
            // and originalKey will be 'derives_from', that's what we will use to get the value
            // if there's no headerEntry use mappedHeader as is
            const headerEntry = Object.entries(headerMap).find(([, mapped]) => mapped === mappedHeader);
            const originalKey = headerEntry ? headerEntry[0] : mappedHeader;

            let value = row[originalKey]; // e.g. row['name'] or row['derives_from']

            // handle special case for derives from, where we need to get the
            // name from the first source in the array
            // TODO: remove hard coded logic and find a better way to handle this
            if (originalKey === 'derives_from') {
              value =
                Array.isArray(value) && value.length > 0 ? value[0]?.name : '';
            }
            // format as string so that values like 0 or false are handled
            return `"${value ?? ''}"`;
          })
          .join(',')
      ),
    ];

    return csvRows.join('\n');
  }
}
